import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject, Subscription } from 'rxjs';
import {
	AlertComponent,
	ButtonComponent,
	ButtonIconComponent,
	CreateLayoutComponent,
	FooterActionsComponent,
	IBackEvent,
	IconComponent,
	IMenu,
	ITab,
	ItemComponent,
	LinkComponent,
	ListDetailComponent,
	MenuComponent,
	TagComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { formatDate } from '../../../../../core/utils/utils';
import { environment } from '../../../../../../environments/environment';
import { CURL_BEARER_PLACEHOLDER } from '../../../../../panel/utils/build-curl.util';
import { ModalCurlRequestComponent } from '../../../../../shared/components/modal-curl-request/modal-curl-request.component';
import { MenuItemAction } from '../../../../../shared/models/master.models';
import { LoadingService } from '../../../../../shared/services/loading.service';
import { ModalConfirmationService } from '../../../../../shared/services/modal-confirmation.service';
import { DesignThemeDetailComponent } from '../../components/design-theme-detail/design-theme-detail.component';
import { ModalCancelDesignComponent } from '../../components/modal-cancel-design/modal-cancel-design.component';
import {
	IThemeMode,
	ModalDesignThemeComponent,
} from '../../components/modal-design-theme/modal-design-theme.component';
import { IDesignDetailBody, IDesignDetailResponse, TAB_THEMES } from '../../models/design.models';
import { DesignHelperService } from '../../services/design-helper.service';
import { DesignService } from '../../services/design.service';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';
import { MasterService } from '../../../../../shared/services/master.service';
import { NavigationService } from '../../../../../shared/services/navigation.service';

@Component({
	selector: 'app-design-detail',
	imports: [
		TranslatePipe,
		FormsModule,
		ReactiveFormsModule,
		AlertComponent,
		CreateLayoutComponent,
		ItemComponent,
		TagComponent,
		TileComponent,
		ButtonComponent,
		MenuComponent,
		ListDetailComponent,
		DesignThemeDetailComponent,
		ButtonIconComponent,
		FooterActionsComponent,
		LinkComponent,
		IconComponent,
		ModalDesignThemeComponent,
		ModalCancelDesignComponent,
		ModalCurlRequestComponent,
	],
	templateUrl: './design-detail.component.html',
	styleUrl: './design-detail.component.scss',
})
export class DesignDetailComponent implements OnInit {
	private location = inject(Location);
	private activatedRoute = inject(ActivatedRoute);
	private router = inject(Router);
	private navigationService = inject(NavigationService);
	private cdr = inject(ChangeDetectorRef);
	private destroyRef = inject(DestroyRef);
	private designHelper = inject(DesignHelperService);
	private designService = inject(DesignService);
	private masterService = inject(MasterService);
	private loading = inject(LoadingService);
	private trans = inject(TranslateService);
	private modalConfirmationService = inject(ModalConfirmationService);
	public rolePermissions = inject(RolePermissionsService);

	/** Mensaje de error de API (detalle, guardado, borrado o paleta). */
	public detailApiError = signal('');

	public detail?: IDesignDetailBody;
	private id?: string;
	public tabs: ITab[] = this.designHelper.tabs;
	public form!: FormGroup;
	public selectedTab: string = TAB_THEMES.LIGHT;
	public detailMenuOpt: IMenu[] = [];
	public showModalEditTheme: boolean = false;
	public themeEditData?: IThemeMode;
	public showModalCancelDesign: boolean = false;
	public hasChanges: boolean = false;
	/**
	 * Huella del formulario tras hidratar el CVA (no el `detail` crudo de API).
	 * Comparar contra `detail.css_configs` falla: el editor emite el payload transformado.
	 */
	private pristineFingerprint: string | null = null;
	private pristineReady = false;
	/** Edit del editor antes de cerrar la hidratación → dirty tras capture. */
	private pendingUserEdit = false;
	private formValueSub?: Subscription;
	/** Debounce tras carga / logo async / writeValue del CVA antes de fijar pristine. */
	private readonly hydrateSettled$ = new Subject<void>();
	/** Carga inicial del detalle con skeletons (sin spinner fullscreen; Carmen 123499). */
	public detailLoading = false;
	public readonly detailSkeletonTiles = [1, 2, 3] as const;
	public readonly curlModalOpen = signal(false);
	public readonly curlModalTitleKey = signal('PANEL.CURL_MODAL.TITLE');
	public readonly curlModalText = signal('');

	get companyLabel(): string {
		const uuid = this.detail?.company_uuid;
		if (!uuid) return '';
		const company = this.masterService.companies?.find((c) => c.id === uuid);
		return (company?.label as string) || '';
	}

	/** Fecha visible en item-large (detailAction). Ya formateada en getDetail. */
	get detailDateLabel(): string {
		return this.detail?.updated_at?.trim() || '';
	}

	get currentThemeForm(): FormControl {
		return this.form.get(this.selectedTab) as FormControl;
	}

	/** Control del tema activo; enlazar con [formControl] evita errores por nombre al quitar light/dark del FormGroup. */
	get themeFormControl(): FormControl | null {
		const c = this.form?.get(this.selectedTab);
		return c instanceof FormControl ? c : null;
	}

	constructor() {
		effect(() => {
			const modalResponse = this.modalConfirmationService.closeModal();
			if (modalResponse?.id?.menuAction === MenuItemAction.DELETE) {
				if (modalResponse?.action === 'buttonPrimary') {
					this.confirmDeleteDesign();
				}
			}
		});
	}

	ngOnInit(): void {
		this.id = this.activatedRoute.snapshot.params['id'];
		this.form = this.designHelper.form;
		this.bindFormValueChanges(this.form);
		this.destroyRef.onDestroy(() => this.formValueSub?.unsubscribe());

		this.hydrateSettled$.pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.capturePristineFingerprint();
		});

		if (this.id) this.getDetail();
	}

	/** Cambio en el editor de tema (colores, logo, etc.): marca dirty y refresca footer. */
	public onThemeEdited(): void {
		if (!this.pristineReady) {
			this.pendingUserEdit = true;
			this.hydrateSettled$.next();
			return;
		}
		// El fingerprint vs css_configs a veces no refleja el color-picker (payload CVA);
		// un edit real del editor siempre es dirty.
		this.hasChanges = true;
		this.cdr.detectChanges();
	}

	/** Reenlaza valueChanges si se reconstruye el FormGroup (cambio de skin en modal). */
	private bindFormValueChanges(form: FormGroup): void {
		this.formValueSub?.unsubscribe();
		this.formValueSub = form.valueChanges.subscribe(() => {
			if (!this.pristineReady) {
				this.hydrateSettled$.next();
				return;
			}
			this.hasChanges = this.checkHasChanges();
			this.cdr.detectChanges();
		});
	}

	private getDetail(): void {
		if (!this.id) return;
		this.detailApiError.set('');
		this.detailLoading = true;
		this.pristineReady = false;
		this.pristineFingerprint = null;
		this.hasChanges = false;
		this.pendingUserEdit = false;
		this.designService.detail(this.id!).subscribe({
			next: (response: IDesignDetailResponse) => {
				this.detail = response.data;
				if (this.detail.updated_at) this.detail.updated_at = formatDate(this.detail.updated_at);
				this.detailMenuOpt = this.designHelper.getDetailMenuOpt(this.detail);
				const mappedData = {
					title: this.detail.title,
					active: this.detail.active,
					company_uuid: this.detail.company_uuid || '',
					[TAB_THEMES.LIGHT]: { ...this.detail },
					[TAB_THEMES.DARK]: { ...this.detail },
				};
				this.form.patchValue(mappedData);
				this.excludeUnnecessarySkin();
				this.detailLoading = false;
				this.hydrateSettled$.next();
			},
			error: () => {
				this.detailLoading = false;
				this.detailApiError.set(this.trans.instant('DESIGN.DETAIL.LOAD_ERROR'));
			},
		});
	}

	private excludeUnnecessarySkin(): any {
		// Ocultar tab no disponible (en base al skin)
		this.designHelper.filterTabsBySkin(this.detail.skin, this.tabs);

		// Si la tab seleccionado no está disponible, seleccionar el primero disponible
		if (!this.tabs.find((tab) => tab.id === this.selectedTab)) {
			this.selectedTab = this.tabs[0].id;
			this.tabs.forEach((tab) => (tab.selected = tab.id === this.selectedTab));
		}

		// Solo queda un modo en UI: alinear pestaña con el skin antes de quitar controles
		this.selectedTab = this.detail.skin;
		this.tabs.forEach((tab) => (tab.selected = tab.id === this.selectedTab));

		// Desmontar el CVA hijo (formControlName light/dark) antes de removeControl
		this.cdr.detectChanges();

		// Eliminar formulario de tabs no disponibles
		this.designHelper.removeUnavailableTabForms(this.detail.skin, this.form);

		if (!this.form.get(this.selectedTab)) {
			this.selectedTab = this.detail.skin;
			this.tabs.forEach((tab) => (tab.selected = tab.id === this.selectedTab));
		}
	}

	public tabSelected(tab: { id: string; label: string }): void {
		if (this.form.get(tab.id)) {
			this.selectedTab = tab.id;
		}
	}

	private capturePristineFingerprint(): void {
		// Solo una vez por carga: re-capturar absorbería edits del usuario.
		if (this.pristineReady) return;
		this.pristineFingerprint = this.buildFormFingerprint();
		this.pristineReady = true;
		this.hasChanges = this.pendingUserEdit;
		this.pendingUserEdit = false;
		this.cdr.detectChanges();
	}

	/** Estado comparable del form (post-CVA), no el detail crudo de API. */
	private buildFormFingerprint(): string {
		if (!this.detail || !this.form) return '';
		const formValue = this.form.getRawValue();
		const skin = this.detail.skin;
		const theme = (formValue[skin] || {}) as Record<string, unknown>;
		const css = Array.isArray(theme['css_configs']) ? [...(theme['css_configs'] as Array<{ uuid: string; value: unknown }>)] : [];
		const sortFn = (a: { uuid: string }, b: { uuid: string }) => (a.uuid > b.uuid ? 1 : -1);
		const normalizedCss = css
			.map((item) => ({
				uuid: item.uuid,
				value: String(item.value ?? '').trim(),
			}))
			.sort(sortFn);

		return JSON.stringify({
			title: (formValue['title'] as string) || '',
			active: !!formValue['active'],
			company_uuid: (formValue['company_uuid'] as string) || this.detail.company_uuid || '',
			skin,
			watermark: !!theme['watermark'],
			show_product_name: !!theme['show_product_name'],
			logo: this.assetFingerprint(theme['logo']),
			favicon: this.assetFingerprint(theme['favicon']),
			custom_spinner: this.assetFingerprint(theme['custom_spinner']),
			css_configs: normalizedCss,
		});
	}

	private assetFingerprint(value: unknown): string {
		const file = this.designHelper.fileFromUploadValue(value);
		if (file) {
			return `file:${file.name}:${file.size}:${file.lastModified}`;
		}
		if (typeof value === 'string' && value) {
			return `url:${value}`;
		}
		return '';
	}

	private checkHasChanges(): boolean {
		if (!this.pristineReady || this.pristineFingerprint === null) return false;
		return this.buildFormFingerprint() !== this.pristineFingerprint;
	}

	public detailMenuChange(action: IMenu): void {
		const actions: Record<string, () => void> = {
			[MenuItemAction.ACTIVE]: () => {
				this.toggleActive();
			},
			[MenuItemAction.EDIT]: () => {
				this.editDesign();
			},
			[MenuItemAction.DELETE]: () => {
				this.deleteDesign();
			},
			[MenuItemAction.SHOW_CURL]: () => {
				this.openDetailCurlModal();
			},
		};

		actions[action.id]?.();
	}

	public openDetailCurlModal(): void {
		if (!this.id) return;
		this.curlModalTitleKey.set('PANEL.CURL_MODAL.TITLE_DESIGN_DETAIL');
		this.curlModalText.set(this.getDesignDetailCurl(this.id));
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	private getDesignDetailCurl(uuid: string): string {
		const base = environment.apiUrl.replace(/\/$/, '');
		const url = `${base}/design-config/${uuid}`;
		return [`curl -X GET "${url}" \\`, `  -H "Authorization: Bearer ${CURL_BEARER_PLACEHOLDER}"`].join('\n');
	}

	private toggleActive(): void {
		if (!this.detail || !this.id) return;

		const previousActive = this.detail.active;
		const newActive = !previousActive;

		this.applyActiveState(newActive);

		const formData = new FormData();
		formData.append('active', newActive ? '1' : '0');

		this.designService.update(this.id, formData).subscribe({
			next: () => {
				this.hasChanges = this.checkHasChanges();
			},
			error: () => {
				this.applyActiveState(previousActive);
				this.detailApiError.set(this.trans.instant('DESIGN.DETAIL.UPDATE_ERROR'));
			},
		});
	}

	private applyActiveState(active: boolean): void {
		if (!this.detail) return;

		this.detail.active = active;
		this.form.get('active')?.setValue(active, { emitEvent: false });
		this.detailMenuOpt = this.designHelper.getDetailMenuOpt(this.detail);
	}

	private editDesign(): void {
		if (!this.id) return;
		const modeAvailable = this.designHelper.availableModes.find((mode) => mode.id === this.detail?.skin);
		// Preparar los datos del tema actual para la modal
		this.themeEditData = {
			title: this.detail?.title || '',
			modeAvailable,
			company_uuid: this.detail?.company_uuid || '',
		};
		this.showModalEditTheme = true;
	}

	public closeModalEditTheme(result: IThemeMode | null): void {
		this.showModalEditTheme = false;
		if (!result || !this.detail) {
			return;
		}

		if (result.title) {
			this.detail.title = result.title;
			this.form.get('title')?.setValue(result.title);
		}

		if (result.company_uuid !== undefined) {
			this.detail.company_uuid = result.company_uuid || '';
		}

		const companyUuidForForm =
			result.company_uuid !== undefined
				? result.company_uuid || ''
				: (this.form.get('company_uuid')?.value as string) || this.detail.company_uuid || '';

		if (result.modeAvailable !== undefined) {
			const modeValue =
				result.modeAvailable && typeof result.modeAvailable === 'object' && 'value' in result.modeAvailable
					? result.modeAvailable.value
					: result.modeAvailable;

			const newSkin = modeValue ? 'light' : 'dark';

			if (newSkin !== this.detail.skin) {
				const currentValues = this.currentThemeForm.value;
				this.detail.skin = newSkin;

				this.tabs = this.designHelper.tabs;
				this.form = this.designHelper.form;
				this.bindFormValueChanges(this.form);
				// Tras reconstruir el formulario (light + dark), actualizar la pestaña para evitar
				// que el template enlace formControlName a un control aún no existente en el grupo anterior.
				this.selectedTab = newSkin;

				const patchAfterSkinChange = (themeForSkin: unknown): void => {
					this.form.patchValue({
						title: this.detail!.title,
						active: this.detail!.active,
						company_uuid: companyUuidForForm,
						[newSkin]: themeForSkin,
					});
					this.excludeUnnecessarySkin();
				};

				if (result.applyDefaultPaletteOnSkinChange) {
					try {
						this.masterService.getCssConfigs().subscribe({
							next: (response) => {
								try {
									const themeForNewSkin = this.designHelper.applyDefaultPaletteForSkin(
										currentValues,
										response.data,
										newSkin,
									);
									patchAfterSkinChange(themeForNewSkin);
								} catch {
									this.detailApiError.set(this.trans.instant('DESIGN.DETAIL.PALETTE_APPLY_ERROR'));
									patchAfterSkinChange(currentValues);
								}
							},
							error: () => {
								this.detailApiError.set(this.trans.instant('DESIGN.DETAIL.PALETTE_APPLY_ERROR'));
								patchAfterSkinChange(currentValues);
							},
						});
					} catch {
						this.detailApiError.set(this.trans.instant('DESIGN.DETAIL.PALETTE_APPLY_ERROR'));
						patchAfterSkinChange(currentValues);
					}
				} else {
					patchAfterSkinChange(currentValues);
				}
			}
		}

		if (result.company_uuid !== undefined) {
			this.form.get('company_uuid')?.setValue(result.company_uuid || '');
		}

		this.hasChanges = this.checkHasChanges();
	}

	private deleteDesign(): void {
		if (!this.id) return;
		this.modalConfirmationService.show({
			title: this.trans.instant('DESIGN.MODAL_DELETE.TITLE'),
			description: this.trans.instant('DESIGN.MODAL_DELETE.DESCRIPTION'),
			buttonPrimaryLabel: this.trans.instant('BUTTON.DELETE'),
			buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
			buttonPrimaryStyle: true,
			id: { menuAction: MenuItemAction.DELETE },
		});
	}

	private confirmDeleteDesign(): void {
		if (!this.id) return;
		this.loading.show(this.trans.instant('LOADING.DELETE_DESIGN'));
		this.designService.delete(this.id).subscribe({
			next: () => {
				this.router.navigate(['/settings/design']);
			},
			error: () => {
				this.loading.hide();
				this.detailApiError.set(this.trans.instant('DESIGN.DETAIL.DELETE_ERROR'));
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public onBackClick(event: MouseEvent): void {
		event.preventDefault();
		this.goBack({ event });
	}

	public exit(): void {
		this.goBack({ event: new MouseEvent('click') });
	}

	public goBack({ event }: IBackEvent): void {
		if (this.hasChanges && this.rolePermissions.canEditDesignConfig()) {
			this.showModalCancelDesign = true;
		} else {
			this.navigationService.navigateTo({ routeSegments: ['settings/design'] }, event);
		}
	}

	public closeModalCancelDesign(action: boolean | null): void {
		this.showModalCancelDesign = false;

		// Cancelar acción
		if (action === null) return;

		// Guardar y salir
		if (action) this.saveChangesAndExit(true);
		// Solo salir sin guardar
		else this.navigateBack();
	}

	public saveChangesAndExit(goBack: boolean = false): void {
		if (!this.id) return;
		this.detailApiError.set('');
		this.loading.show(this.trans.instant('LOADING.UPDATE_DESIGN'));
		const body = this.getBody();
		this.designService.update(this.id, body).subscribe({
			next: () => {
				this.hasChanges = false;
				if (!goBack) this.getDetail();
				if (goBack) this.navigateBack();
			},
			error: () => {
				this.loading.hide();
				this.detailApiError.set(this.trans.instant('DESIGN.DETAIL.UPDATE_ERROR'));
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public clearDetailApiError(): void {
		this.detailApiError.set('');
	}

	public discardChanges(): void {
		if (!this.detail) return;
		this.hasChanges = false;
		this.getDetail();
	}

	private getBody(): FormData {
		const formValue = this.form.getRawValue();
		const theme = formValue.light || formValue.dark;
		const formData = new FormData();

		// Campos raíz: deben salir del formulario (fuente de verdad al editar), no solo de `detail`.
		const title = (formValue['title'] as string | undefined) ?? this.detail?.title ?? '';
		const active = (formValue['active'] as boolean | undefined) ?? this.detail?.active;
		formData.append('title', title);
		formData.append('active', active ? '1' : '0');
		formData.append('skin', this.detail?.skin || '');
		formData.append('company_uuid', formValue.company_uuid || '');
		formData.append('show_product_name', theme?.show_product_name ? '1' : '0');
		this.designHelper.appendThemeAssetToFormData(formData, 'custom_spinner', theme?.custom_spinner);
		this.designHelper.appendThemeAssetToFormData(formData, 'logo', theme?.logo);
		this.designHelper.appendThemeAssetToFormData(formData, 'favicon', theme?.favicon);

		// Agregar watermark
		formData.append('watermark', theme.watermark ? '1' : '0');

		// Agregar css_configs como JSON string
		if (theme.css_configs && Array.isArray(theme.css_configs)) {
			formData.append('css_configs', JSON.stringify(theme.css_configs));
		}

		return formData;
	}

	private navigateBack(): void {
		this.location.back();
	}
}
